import React, { useState, useEffect, lazy, Suspense } from 'react';
import {
  Home, Target, Database, TrendingUp, Zap, Layers, Bell,
  ShieldCheck, ChevronRight, Search, Plus, Filter,
  ChevronDown, LogOut, User, HelpCircle, Moon, Sun,
  Network, Brain, Lightbulb, Activity, BarChart3,
  MessageSquare, Hash, Inbox, Map, FileText,
  Server, Key, Eye, GitBranch, LucideIcon, Globe, Package,
  Shield, AlertTriangle, Grid3X3, ClipboardCheck, CheckCircle2,
  Settings, FileCheck
} from 'lucide-react';
import { useAuth } from '../../auth/AuthProvider';
import { useNavigate, useLocation, Routes, Route } from 'react-router-dom';
import { theme } from '../../styles/theme';
import Sidebar from './Sidebar';
import CommandPalette from './CommandPalette';
import Dashboard from './Dashboard';
import ServiceView from './ServiceView';
import Unify from './Unify';
import SystemAdmin from './SystemAdmin';
import AdminCountries from './AdminCountries';
import AdminPrompts from './AdminPrompts';
import Harmony from '../../pages/Harmony/Harmony';
import Symphony from '../../pages/Symphony/Symphony';
import CollectionPage from '../Collection';
import userApi from '../../api/userApi';
import logger from '../../utils/logger';

// Lazy-loaded GRC Page Components (Rubric)
const RubricDashboardPage = lazy(() => import('../../pages/rubric/RubricDashboardPage'));
const RiskListPage = lazy(() => import('../../pages/rubric/risk/RiskListPage'));
const RiskHeatmapPage = lazy(() => import('../../pages/rubric/risk/RiskHeatmapPage'));
const TreatmentPlanPage = lazy(() => import('../../pages/rubric/risk/TreatmentPlanPage'));
const KRIDashboardPage = lazy(() => import('../../pages/rubric/kri/KRIDashboardPage'));
const ComplianceDashboardPage = lazy(() => import('../../pages/rubric/compliance/ComplianceDashboardPage'));
const FrameworkLibraryPage = lazy(() => import('../../pages/rubric/compliance/FrameworkLibraryPage'));
const ObligationRegisterPage = lazy(() => import('../../pages/rubric/compliance/ObligationRegisterPage'));
const GapRegisterPage = lazy(() => import('../../pages/rubric/compliance/GapRegisterPage'));
const UnifiedGRCDashboardPage = lazy(() => import('../../pages/rubric/UnifiedGRCDashboardPage'));
const ReportBuilderPage = lazy(() => import('../../pages/rubric/reporting/ReportBuilderPage'));

// Lazy-loaded GRC Page Components (Controls)
const ControlDashboardPage = lazy(() => import('../../pages/rubric/control/ControlDashboardPage'));
const ControlRegisterPage = lazy(() => import('../../pages/rubric/control/ControlRegisterPage'));
const TestSchedulePage = lazy(() => import('../../pages/rubric/control/TestSchedulePage'));
const ExceptionManagementPage = lazy(() => import('../../pages/rubric/control/ExceptionManagementPage'));

// Lazy-loaded GRC Page Components (Evidence)
const EvidenceListPage = lazy(() => import('../../pages/rubric/evidence/EvidenceListPage'));
const EvidenceDetailPage = lazy(() => import('../../pages/rubric/evidence/EvidenceDetailPage'));
const EvidenceFormPage = lazy(() => import('../../pages/rubric/evidence/EvidenceFormPage'));

// Lazy-loaded GRC Detail/Form Pages (Risk)
const RiskDetailPage = lazy(() => import('../../pages/rubric/risk/RiskDetailPage'));
const RiskFormPage = lazy(() => import('../../pages/rubric/risk/RiskFormPage'));
const TreatmentPlanEditPage = lazy(() => import('../../pages/rubric/risk/TreatmentPlanEditPage'));
const TrendAnalysisPage = lazy(() => import('../../pages/rubric/risk/TrendAnalysisPage'));
const FindingElevationPage = lazy(() => import('../../pages/rubric/risk/FindingElevationPage'));

// Lazy-loaded GRC Detail/Form Pages (KRI)
const KRIListPage = lazy(() => import('../../pages/rubric/kri/KRIListPage'));
const KRIDetailPage = lazy(() => import('../../pages/rubric/kri/KRIDetailPage'));
const KRIFormPage = lazy(() => import('../../pages/rubric/kri/KRIFormPage'));
const KRIAlertsPage = lazy(() => import('../../pages/rubric/kri/KRIAlertsPage'));

// Lazy-loaded GRC Detail/Form Pages (Compliance)
const FrameworkDetailPage = lazy(() => import('../../pages/rubric/compliance/FrameworkDetailPage'));
const FrameworkMappingsPage = lazy(() => import('../../pages/rubric/compliance/FrameworkMappingsPage'));
const ObligationDetailPage = lazy(() => import('../../pages/rubric/compliance/ObligationDetailPage'));
const ObligationFormPage = lazy(() => import('../../pages/rubric/compliance/ObligationFormPage'));
const GapDetailPage = lazy(() => import('../../pages/rubric/compliance/GapDetailPage'));
const CreateGapPage = lazy(() => import('../../pages/rubric/compliance/CreateGapPage'));

// Lazy-loaded GRC Detail/Form Pages (Controls)
const ControlDetailPage = lazy(() => import('../../pages/rubric/control/ControlDetailPage'));
const ControlFormPage = lazy(() => import('../../pages/rubric/control/ControlFormPage'));
const TestDetailPage = lazy(() => import('../../pages/rubric/control/TestDetailPage'));
const FrameworkCoveragePage = lazy(() => import('../../pages/rubric/control/FrameworkCoveragePage'));

// Lazy-loaded GRC Pages (Reporting)
const ReportHistoryPage = lazy(() => import('../../pages/rubric/reporting/ReportHistoryPage'));
const GRCReportDetailPage = lazy(() => import('../../pages/rubric/reporting/GRCReportDetailPage'));

// Lazy-loaded GRC Pages (Workflow & Audit Trail)
const WorkflowPage = lazy(() => import('../../pages/rubric/workflow/WorkflowPage'));
const AuditTrailPage = lazy(() => import('../../pages/rubric/audit/AuditTrailPage'));

/** Loading fallback for lazy-loaded GRC pages */
const GRCLoadingFallback = () => (
  <div className="flex items-center justify-center h-64">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
  </div>
);

export interface SidebarItem {
  id: string;
  icon: LucideIcon;
  label: string;
  badge?: string | number | null;
  children?: SidebarChild[] | null;
}

export interface SidebarChild {
  id: string;
  label: string;
  badge?: string | number | null;
  icon?: LucideIcon;
  status?: 'healthy' | 'warning' | 'error';
  children?: SidebarChild[] | null;
}

const Console: React.FC = () => {
  const [sidebarExpanded] = useState(true); // Permanently expanded
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [selectedSection, setSelectedSection] = useState('home');
  const [expandedMenus, setExpandedMenus] = useState<string[]>(['services', 'insights']);
  const [searchQuery, setSearchQuery] = useState('');
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [userProfile, setUserProfile] = useState<any>(null);
  
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isRubricPath = location.pathname.startsWith('/rubric');

  // Map sidebar section IDs to rubric URLs
  const rubricSectionUrls: Record<string, string> = {
    'app-rubric': '/rubric',
    'rubric-grc': '/rubric',
    'rubric-dashboard': '/rubric/dashboard',
    'rubric-risks': '/rubric/risks',
    'rubric-heatmap': '/rubric/heatmap',
    'rubric-treatment': '/rubric/treatment',
    'rubric-kri': '/rubric/kri',
    'rubric-compliance': '/rubric/compliance',
    'compliance-dashboard': '/rubric/compliance',
    'compliance-frameworks': '/rubric/compliance/frameworks',
    'compliance-obligations': '/rubric/compliance/obligations',
    'compliance-gaps': '/rubric/compliance/gaps',
    'compliance-mappings': '/rubric/compliance/mappings',
    'rubric-controls': '/rubric/controls',
    'controls-dashboard': '/rubric/controls',
    'controls-register': '/rubric/controls/register',
    'controls-tests': '/rubric/controls/tests',
    'controls-exceptions': '/rubric/controls/exceptions',
    'rubric-evidence': '/rubric/evidence',
    'rubric-workflow': '/rubric/workflow',
    'rubric-audit': '/rubric/audit',
    'rubric-reports': '/rubric/reports',
  };

  // Handle sidebar section changes - navigate for rubric items, setState for others
  const handleSectionChange = (sectionId: string) => {
    const rubricUrl = rubricSectionUrls[sectionId];
    if (rubricUrl) {
      navigate(rubricUrl);
    } else if (isRubricPath) {
      navigate('/', { state: { section: sectionId } });
    } else {
      setSelectedSection(sectionId);
    }
  };

  // Initialize section from navigation state (when transitioning from /rubric back to /)
  useEffect(() => {
    const navSection = (location.state as any)?.section;
    if (navSection) {
      setSelectedSection(navSection);
      window.history.replaceState({}, document.title);
    }
  }, []);

  // Sync sidebar selection from URL when on rubric paths
  useEffect(() => {
    if (!isRubricPath) return;
    const path = location.pathname.replace(/^\/rubric\/?/, '');

    let section = 'rubric-grc';
    if (!path || path === 'grc') section = 'rubric-grc';
    else if (path === 'dashboard') section = 'rubric-dashboard';
    else if (path.startsWith('risks') || path === 'heatmap' || path.startsWith('treatment') || path === 'trends' || path.startsWith('findings')) section = 'rubric-risks';
    else if (path.startsWith('kri')) section = 'rubric-kri';
    else if (path === 'compliance') section = 'compliance-dashboard';
    else if (path.startsWith('compliance/mappings')) section = 'compliance-dashboard';
    else if (path.startsWith('compliance/frameworks')) section = 'compliance-frameworks';
    else if (path.startsWith('compliance/obligations')) section = 'compliance-obligations';
    else if (path.startsWith('compliance/gaps')) section = 'compliance-gaps';
    else if (path === 'controls') section = 'controls-dashboard';
    else if (path.startsWith('controls/register') || path.startsWith('controls/new') || path.match(/^controls\/[^/]+$/)) section = 'controls-register';
    else if (path.startsWith('controls/tests')) section = 'controls-tests';
    else if (path.startsWith('controls/exceptions')) section = 'controls-exceptions';
    else if (path.startsWith('controls/coverage')) section = 'controls-register';
    else if (path.startsWith('evidence')) section = 'rubric-evidence';
    else if (path.startsWith('workflow')) section = 'rubric-workflow';
    else if (path.startsWith('audit')) section = 'rubric-audit';
    else if (path.startsWith('reports')) section = 'rubric-reports';

    setSelectedSection(section);

    // Ensure rubric sidebar menus are expanded
    setExpandedMenus(prev => {
      const needed = ['applications', 'app-rubric'];
      if (section.startsWith('compliance-')) needed.push('rubric-compliance');
      if (section.startsWith('controls-')) needed.push('rubric-controls');
      const next = [...new Set([...prev, ...needed])];
      return next.length !== prev.length ? next : prev;
    });
  }, [location.pathname, isRubricPath]);

  // Add Google Font
  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Tenor+Sans&family=Roboto:wght@300;400;500;700&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
  }, []);

  // Load user profile
  useEffect(() => {
    const loadUserProfile = async () => {
      try {
        const profile = await userApi.getCurrentUser();
        setUserProfile(profile);
      } catch (error) {
        logger.error('Failed to load user profile', { error });
      }
    };
    
    if (user) {
      loadUserProfile();
    }
  }, [user]);

  // Command palette keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCommandPaletteOpen(true);
      }
      if (e.key === 'Escape') {
        setCommandPaletteOpen(false);
        setUserMenuOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Handle Harmony navigation - removed to keep within Console
  // Harmony will now be displayed within the Console layout

  // Save dark mode preference
  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
    window.dispatchEvent(new Event('darkModeChange'));
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  const sidebarItems: SidebarItem[] = [
    { 
      id: 'home', 
      icon: Home, 
      label: 'Home', 
      badge: null,
      children: null 
    },
    { 
      id: 'skill-motive', 
      icon: Target, 
      label: 'Skill and Motive Match', 
      badge: null,
      children: [
        { id: 'skill-search', label: 'Search', badge: null, icon: Search },
        { id: 'skill-progress', label: 'Track Progress', badge: null, icon: Activity },
        { id: 'skill-insights', label: 'Insights', badge: null, icon: Eye },
      ]
    },
    { 
      id: 'services', 
      icon: Database, 
      label: 'Services', 
      badge: null,
      children: [
        { id: 'service-nexus', label: 'Nexus', badge: null, icon: Network, status: 'healthy' },
        { id: 'service-bridge', label: 'Bridge', badge: null, icon: GitBranch, status: 'healthy' },
        { id: 'service-symphony', label: 'Symphony', badge: null, icon: Brain, status: 'healthy' },
        { id: 'service-hypothesis', label: 'Hypothesis Engine', badge: null, icon: Lightbulb, status: 'healthy' },
      ]
    },
    { 
      id: 'insights', 
      icon: TrendingUp, 
      label: 'Insights', 
      badge: null,
      children: [
        { id: 'insights-overview', label: 'Overview', badge: null },
        { id: 'insights-realtime', label: 'Real-time', badge: 'LIVE' },
        { id: 'insights-dashboards', label: 'Dashboards', badge: null, icon: BarChart3 },
        { id: 'insights-question', label: 'Ask a question', badge: null, icon: MessageSquare },
      ]
    },
    {
      id: 'automation',
      icon: Zap,
      label: 'Automation',
      badge: null,
      children: [
        { id: 'harmony', label: 'Harmony', badge: 'NEW', icon: Brain }
      ]
    },
    { 
      id: 'applications', 
      icon: Layers, 
      label: 'Applications', 
      badge: null,
      children: [
        {
          id: 'app-unify',
          label: 'Unify',
          badge: null,
          icon: Map,
          children: [
            { id: 'unify-metrics', label: 'Metrics', badge: null },
            {
              id: 'unify-lenses',
              label: 'Lenses',
              badge: null,
              children: [
                { id: 'lenses-compare', label: 'Compare', badge: null },
                { id: 'lenses-manage', label: 'Manage', badge: null },
              ]
            },
            { id: 'unify-scenarios', label: 'Scenarios', badge: null },
          ]
        },
        {
          id: 'app-rubric',
          label: 'Rubric',
          badge: null,
          icon: Shield,
          children: [
            { id: 'rubric-dashboard', label: 'Dashboard', icon: BarChart3 },
            { id: 'rubric-risks', label: 'Risk Register', icon: AlertTriangle },
            { id: 'rubric-heatmap', label: 'Heat Map', icon: Grid3X3 },
            { id: 'rubric-treatment', label: 'Treatment Plans', icon: ClipboardCheck },
            { id: 'rubric-kri', label: 'Key Risk Indicators', icon: Activity },
            { id: 'rubric-compliance', label: 'Compliance', icon: CheckCircle2, children: [
              { id: 'compliance-dashboard', label: 'Dashboard' },
              { id: 'compliance-frameworks', label: 'Framework Library' },
              { id: 'compliance-obligations', label: 'Obligations' },
              { id: 'compliance-gaps', label: 'Gaps' },
            ]},
            { id: 'rubric-controls', label: 'Controls', icon: Settings, children: [
              { id: 'controls-dashboard', label: 'Dashboard' },
              { id: 'controls-register', label: 'Register' },
              { id: 'controls-tests', label: 'Test Schedule' },
              { id: 'controls-exceptions', label: 'Exceptions' },
            ]},
            { id: 'rubric-evidence', label: 'Evidence', icon: FileCheck },
            { id: 'rubric-workflow', label: 'Workflow', icon: Activity },
            { id: 'rubric-audit', label: 'Audit Trail', icon: ClipboardCheck },
            { id: 'rubric-reports', label: 'Reports', icon: FileText },
            { id: 'rubric-grc', label: 'Unified GRC', icon: Shield },
          ]
        },
      ]
    },
    {
      id: 'notifications',
      icon: Bell,
      label: 'Notifications',
      badge: '3',
      children: [
        { id: 'notifications-inbox', label: 'Inbox', badge: '3', icon: Inbox },
        { id: 'notifications-channels', label: 'Channels', badge: null, icon: Hash },
      ]
    },
    { 
      id: 'administration', 
      icon: ShieldCheck, 
      label: 'Administration', 
      badge: null,
      children: [
        { 
          id: 'admin-countries', 
          label: 'Countries', 
          badge: null, 
          icon: Globe
        },
        {
          id: 'admin-prompts',
          label: 'Prompts',
          badge: null,
          icon: FileText
        },
        {
          id: 'admin-collection',
          label: 'Collection',
          badge: null,
          icon: Package
        },
        {
          id: 'admin-system', 
          label: 'System', 
          badge: null, 
          icon: Server,
          children: [
            { id: 'admin-keycloak', label: 'Keycloak', badge: null, icon: Key },
            { id: 'admin-grafana', label: 'Grafana', badge: null, icon: BarChart3 },
            { id: 'admin-kibana', label: 'Kibana', badge: null, icon: FileText },
            { id: 'admin-jaeger', label: 'Jaeger', badge: null, icon: Activity },
            { id: 'admin-prometheus', label: 'Prometheus', badge: null, icon: Eye },
          ]
        },
      ]
    },
  ];

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleProfileClick = () => {
    navigate('/profile');
    setUserMenuOpen(false);
  };

  // Generate breadcrumbs
  const getBreadcrumbs = () => {
    const crumbs = ['Hinata PAL'];
    const selected = selectedSection;
    
    const parentItem = sidebarItems.find(item => 
      item.id === selected || (item.children && item.children.some(child => child.id === selected))
    );
    
    if (parentItem) {
      crumbs.push(parentItem.label);
      
      if (parentItem.children) {
        const childItem = parentItem.children.find(child => child.id === selected);
        if (childItem) {
          crumbs.push(childItem.label);
        }
      }
    }
    
    return crumbs;
  };

  const breadcrumbs = getBreadcrumbs();

  return (
    <div className="flex h-screen" style={{ 
      fontFamily: theme.fonts.primary,
      backgroundColor: darkMode ? theme.colors.background.dark : theme.colors.background.default 
    }}>
      {/* Sidebar */}
      <Sidebar
        items={sidebarItems}
        expanded={sidebarExpanded}
        onExpandedChange={() => {}}
        selectedSection={selectedSection}
        onSectionChange={handleSectionChange}
        expandedMenus={expandedMenus}
        onExpandedMenusChange={setExpandedMenus}
        darkMode={darkMode}
        onCommandPaletteOpen={() => setCommandPaletteOpen(true)}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar */}
        <header className="h-16 flex items-center justify-between px-6" style={{
          backgroundColor: darkMode ? theme.colors.background.darkPaper : theme.colors.background.paper,
          borderBottom: `1px solid ${darkMode ? theme.colors.border.dark : theme.colors.border.light}`,
        }}>
          <div className="flex items-center space-x-4 flex-1">
            {/* Breadcrumbs */}
            <nav className="flex items-center space-x-2 text-sm">
              {breadcrumbs.map((crumb, index) => (
                <React.Fragment key={index}>
                  {index > 0 && <ChevronRight className="w-4 h-4" style={{ color: theme.colors.text.secondary }} />}
                  <span style={{ 
                    color: index === breadcrumbs.length - 1 
                      ? darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary
                      : theme.colors.text.secondary,
                    fontWeight: index === breadcrumbs.length - 1 ? 500 : 400
                  }}>
                    {crumb}
                  </span>
                </React.Fragment>
              ))}
            </nav>

            {/* Search Bar */}
            <div className="flex-1 max-w-xl">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4" style={{ 
                  color: theme.colors.text.secondary 
                }} />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 rounded-lg focus:outline-none focus:ring-2"
                  style={{
                    backgroundColor: darkMode ? theme.colors.background.dark : theme.colors.background.default,
                    border: `1px solid ${darkMode ? theme.colors.border.dark : theme.colors.border.light}`,
                    color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary,
                  }}
                />
              </div>
            </div>
          </div>

          {/* Right Side Actions */}
          <div className="flex items-center space-x-4">
            <button className="p-2 rounded-lg transition-colors hover:bg-black hover:bg-opacity-5">
              <Plus className="w-5 h-5" style={{ color: theme.colors.text.secondary }} />
            </button>
            <button className="p-2 rounded-lg transition-colors hover:bg-black hover:bg-opacity-5">
              <Filter className="w-5 h-5" style={{ color: theme.colors.text.secondary }} />
            </button>
            <button className="p-2 rounded-lg transition-colors hover:bg-black hover:bg-opacity-5 relative">
              <Bell className="w-5 h-5" style={{ color: theme.colors.text.secondary }} />
              <span className="absolute top-1 right-1 w-2 h-2 rounded-full" style={{ 
                backgroundColor: theme.colors.status.error 
              }}></span>
            </button>
            
            {/* Dark Mode Toggle */}
            <button
              onClick={() => setDarkMode(!darkMode)}
              className="p-2 rounded-lg transition-colors hover:bg-black hover:bg-opacity-5"
            >
              {darkMode ? (
                <Sun className="w-5 h-5" style={{ color: theme.colors.text.secondary }} />
              ) : (
                <Moon className="w-5 h-5" style={{ color: theme.colors.text.secondary }} />
              )}
            </button>

            {/* User Menu */}
            <div className="relative">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors hover:bg-black hover:bg-opacity-5"
              >
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{
                  background: `linear-gradient(135deg, ${theme.colors.primary.main} 0%, ${theme.colors.secondary.main} 100%)`,
                }}>
                  {userProfile?.name?.charAt?.(0)?.toUpperCase() || user?.profile?.name?.charAt?.(0)?.toUpperCase() || 'U'}
                </div>
                <ChevronDown className="w-4 h-4" style={{ color: theme.colors.text.secondary }} />
              </button>
              
              {userMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 rounded-xl py-1 z-50" style={{
                  backgroundColor: darkMode ? theme.colors.background.darkPaper : theme.colors.background.paper,
                  border: `1px solid ${darkMode ? theme.colors.border.dark : theme.colors.border.light}`,
                  boxShadow: '0 4px 20px rgba(228, 165, 128, 0.15)',
                }}>
                  <button 
                    onClick={handleProfileClick}
                    className="w-full text-left px-4 py-2 text-sm flex items-center space-x-2 hover:bg-black hover:bg-opacity-5" 
                    style={{
                      color: darkMode ? theme.colors.text.darkSecondary : theme.colors.text.primary
                    }}
                  >
                    <User className="w-4 h-4" />
                    <span>Profile</span>
                  </button>
                  <button className="w-full text-left px-4 py-2 text-sm flex items-center space-x-2 hover:bg-black hover:bg-opacity-5" style={{
                    color: darkMode ? theme.colors.text.darkSecondary : theme.colors.text.primary
                  }}>
                    <HelpCircle className="w-4 h-4" />
                    <span>Help</span>
                  </button>
                  <hr style={{ borderColor: darkMode ? theme.colors.border.dark : theme.colors.border.light }} />
                  <button 
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2 text-sm flex items-center space-x-2 hover:bg-black hover:bg-opacity-5" 
                    style={{
                      color: darkMode ? theme.colors.text.darkSecondary : theme.colors.text.primary
                    }}
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Content Area */}
        <main className="flex-1 overflow-auto p-6" style={{
          backgroundColor: darkMode ? theme.colors.background.dark : theme.colors.background.default
        }}>
          {isRubricPath ? (
            <Suspense fallback={<GRCLoadingFallback />}>
              <Routes>
                <Route index element={<RubricDashboardPage />} />
                <Route path="dashboard" element={<RubricDashboardPage />} />
                {/* Risk */}
                <Route path="risks" element={<RiskListPage />} />
                <Route path="risks/new" element={<RiskFormPage />} />
                <Route path="risks/:riskId" element={<RiskDetailPage />} />
                <Route path="heatmap" element={<RiskHeatmapPage />} />
                <Route path="treatment" element={<TreatmentPlanPage />} />
                <Route path="treatment/:planId/edit" element={<TreatmentPlanEditPage />} />
                <Route path="trends" element={<TrendAnalysisPage />} />
                <Route path="findings/elevate" element={<FindingElevationPage />} />
                {/* KRI */}
                <Route path="kri" element={<KRIDashboardPage />} />
                <Route path="kri/list" element={<KRIListPage />} />
                <Route path="kri/new" element={<KRIFormPage />} />
                <Route path="kri/:kriId" element={<KRIDetailPage />} />
                <Route path="kri/alerts" element={<KRIAlertsPage />} />
                {/* Compliance */}
                <Route path="compliance" element={<ComplianceDashboardPage />} />
                <Route path="compliance/frameworks" element={<FrameworkLibraryPage />} />
                <Route path="compliance/frameworks/:frameworkId" element={<FrameworkDetailPage />} />
                <Route path="compliance/obligations" element={<ObligationRegisterPage />} />
                <Route path="compliance/obligations/new" element={<ObligationFormPage />} />
                <Route path="compliance/obligations/:obligationId" element={<ObligationDetailPage />} />
                <Route path="compliance/gaps" element={<GapRegisterPage />} />
                <Route path="compliance/gaps/new" element={<CreateGapPage />} />
                <Route path="compliance/gaps/:gapId" element={<GapDetailPage />} />
                <Route path="compliance/mappings" element={<FrameworkMappingsPage />} />
                {/* Controls */}
                <Route path="controls" element={<ControlDashboardPage />} />
                <Route path="controls/register" element={<ControlRegisterPage />} />
                <Route path="controls/new" element={<ControlFormPage />} />
                <Route path="controls/:controlId" element={<ControlDetailPage />} />
                <Route path="controls/tests" element={<TestSchedulePage />} />
                <Route path="controls/tests/:testId" element={<TestDetailPage />} />
                <Route path="controls/exceptions" element={<ExceptionManagementPage />} />
                <Route path="controls/coverage" element={<FrameworkCoveragePage />} />
                {/* Evidence */}
                <Route path="evidence" element={<EvidenceListPage />} />
                <Route path="evidence/new" element={<EvidenceFormPage />} />
                <Route path="evidence/:evidenceId" element={<EvidenceDetailPage />} />
                {/* Reports */}
                <Route path="reports" element={<ReportBuilderPage />} />
                <Route path="reports/history" element={<ReportHistoryPage />} />
                <Route path="reports/:reportId" element={<GRCReportDetailPage />} />
                {/* Workflow */}
                <Route path="workflow" element={<WorkflowPage />} />
                {/* Audit Trail */}
                <Route path="audit" element={<AuditTrailPage />} />
                {/* Unified GRC */}
                <Route path="grc" element={<UnifiedGRCDashboardPage />} />
              </Routes>
            </Suspense>
          ) : selectedSection === 'service-symphony' ? (
            <Symphony />
          ) : selectedSection.startsWith('service-') ? (
            <ServiceView
              serviceId={selectedSection}
              darkMode={darkMode}
              serviceName={sidebarItems.find(i => i.id === 'services')?.children?.find(c => c.id === selectedSection)?.label || 'Service'}
            />
          ) : selectedSection === 'app-unify' || selectedSection.startsWith('unify-') ? (
            <Unify darkMode={darkMode} />
          ) : selectedSection === 'admin-countries' ? (
            <AdminCountries darkMode={darkMode} />
          ) : selectedSection === 'admin-prompts' ? (
            <AdminPrompts darkMode={darkMode} />
          ) : selectedSection === 'admin-collection' ? (
            <CollectionPage />
          ) : selectedSection === 'harmony' ? (
            <Harmony />
          ) : selectedSection === 'admin-system' || selectedSection.startsWith('admin-keycloak') ||
              selectedSection.startsWith('admin-grafana') || selectedSection.startsWith('admin-kibana') ||
              selectedSection.startsWith('admin-jaeger') || selectedSection.startsWith('admin-prometheus') ? (
            <SystemAdmin darkMode={darkMode} />
          ) : (
            <Dashboard darkMode={darkMode} userProfile={userProfile} />
          )}
        </main>
      </div>

      {/* Command Palette */}
      <CommandPalette
        isOpen={commandPaletteOpen}
        onClose={() => setCommandPaletteOpen(false)}
        darkMode={darkMode}
        onSectionChange={handleSectionChange}
      />
    </div>
  );
};

export default Console;